import { useMutation, useQuery } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import {
  confirmInstallation,
  createInstallUrl,
  disconnectInstallation,
  getInstallations,
  getPendingInstallation,
  getMappableWorkspaces,
  getRepositories,
  getSpaceRepositoryOptions,
  getStatusRules,
  getTaskPullRequests,
  setRepositoryMapping,
  setSpaceRepositories,
  setStatusRules,
} from '../api/github.api';
import {
  GithubInstallation,
  GithubMappableWorkspace,
  GithubRepositoryMapping,
  GithubSpaceRepositoryOptions,
  GithubStatusRule,
  GithubStatusRuleInput,
  PendingInstallation,
  TaskPullRequest,
} from '../types/Github';
import { queryClient } from '../queryClient';
import { queryKeys } from './queryKeys';
import { QUERY_CONFIG } from '../constants/queryConfig';

/**
 * Read what a pending token refers to, without spending it.
 *
 * `retry: false` is deliberate: a 404 here means the token is expired or already spent,
 * which is a terminal answer the screen must show immediately - retrying it just delays
 * the error. `staleTime: Infinity` because the record is immutable for its lifetime.
 */
export const useGetPendingInstallation = (token: string | null) => {
  return useQuery<PendingInstallation, AxiosError>({
    queryKey: queryKeys.githubPendingInstallation(token ?? ''),
    queryFn: async () => (await getPendingInstallation(token as string)).data.pendingInstallation,
    enabled: !!token,
    retry: false,
    staleTime: Infinity,
  });
};

export const useGetGithubInstallations = (organizationId: number | null) => {
  return useQuery<GithubInstallation[], Error>({
    queryKey: queryKeys.githubInstallations(organizationId as number),
    queryFn: async () => (await getInstallations(organizationId as number)).data.installations,
    enabled: !!organizationId,
    staleTime: QUERY_CONFIG.STALE_TIME,
  });
};

export const useGetGithubRepositories = (organizationId: number | null) => {
  return useQuery<GithubRepositoryMapping[], Error>({
    queryKey: queryKeys.githubRepositories(organizationId as number),
    queryFn: async () => (await getRepositories(organizationId as number)).data.repositories,
    enabled: !!organizationId,
    staleTime: QUERY_CONFIG.STALE_TIME,
  });
};

/**
 * The workspaces a repository may be mapped to. ADMIN-guarded server-side, so this
 * 403s for a non-admin - only the admin-gated Integrations page calls it.
 */
export const useGetGithubMappableWorkspaces = (organizationId: number | null) => {
  return useQuery<GithubMappableWorkspace[], Error>({
    queryKey: queryKeys.githubMappableWorkspaces(organizationId as number),
    queryFn: async () => (await getMappableWorkspaces(organizationId as number)).data.workspaces,
    enabled: !!organizationId,
    staleTime: QUERY_CONFIG.STALE_TIME,
  });
};

/** Resolves the github.com URL the browser should be sent to. */
export const useConnectGithubMutation = () => {
  return useMutation<string, AxiosError, number>({
    mutationFn: async (organizationId: number) => (await createInstallUrl(organizationId)).data.installUrl,
  });
};

export const useConfirmInstallationMutation = () => {
  return useMutation<void, AxiosError, { organizationId: number; pendingToken: string }>({
    mutationFn: async ({ organizationId, pendingToken }) => {
      await confirmInstallation(organizationId, pendingToken);
    },
    onSuccess: (_data, { organizationId }) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.githubInstallations(organizationId) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.githubRepositories(organizationId) });
    },
  });
};

export const useDisconnectInstallationMutation = () => {
  return useMutation<void, AxiosError, { organizationId: number; installationId: string }>({
    mutationFn: async ({ organizationId, installationId }) => {
      await disconnectInstallation(organizationId, installationId);
    },
    onSuccess: (_data, { organizationId }) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.githubInstallations(organizationId) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.githubRepositories(organizationId) });
    },
  });
};

export const useSetRepositoryMappingMutation = () => {
  return useMutation<
    GithubRepositoryMapping,
    AxiosError,
    { organizationId: number; repoId: string; workspaceId: number | null }
  >({
    mutationFn: async ({ organizationId, repoId, workspaceId }) =>
      (await setRepositoryMapping(organizationId, repoId, workspaceId)).data.repository,
    onSuccess: (_data, { organizationId }) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.githubRepositories(organizationId) });
    },
  });
};

/**
 * The pull-request-event → task-status mapping for one space.
 *
 * Not given `QUERY_CONFIG.STALE_TIME` (24h) like the org-scoped GitHub queries: the
 * settings panel that reads this is also what writes it, and a day-stale rule list
 * would show someone their own save undone. The mutation invalidates on success, so
 * the default staleness is the right behaviour here.
 */
export const useGetGithubStatusRules = (spaceId: number | null) => {
  return useQuery<GithubStatusRule[], AxiosError>({
    queryKey: queryKeys.githubStatusRules(spaceId as number),
    queryFn: async () => (await getStatusRules(spaceId as number)).data.rules,
    enabled: !!spaceId,
  });
};

export const useSetGithubStatusRulesMutation = () => {
  return useMutation<GithubStatusRule[], AxiosError, { spaceId: number; rules: GithubStatusRuleInput[] }>({
    mutationFn: async ({ spaceId, rules }) => (await setStatusRules(spaceId, rules)).data.rules,
    // The response is the full, freshly-resolved rule list, so seed the cache with it
    // rather than invalidating: it saves a refetch and avoids a flash of stale rows.
    onSuccess: (data, { spaceId }) => {
      queryClient.setQueryData(queryKeys.githubStatusRules(spaceId), data);
    },
  });
};

/**
 * What a space owner may attach to their space.
 *
 * OWNER-guarded server-side, so this 403s for a member. `enabled` therefore takes the
 * caller's role as well as the id: firing it for every member who opens the tab would
 * put a 403 in the console on a screen that is working exactly as intended.
 *
 * Default staleness rather than the 24h `QUERY_CONFIG.STALE_TIME` the org-scoped GitHub
 * queries use, for the same reason as the status rules below: the panel that reads this
 * is the panel that writes it.
 */
export const useGetSpaceRepositoryOptions = (spaceId: number | null, enabled = true) => {
  return useQuery<GithubSpaceRepositoryOptions, AxiosError>({
    queryKey: queryKeys.githubSpaceRepositories(spaceId as number),
    queryFn: async () => (await getSpaceRepositoryOptions(spaceId as number)).data,
    enabled: !!spaceId && enabled,
  });
};

export const useSetSpaceRepositoriesMutation = () => {
  return useMutation<
    GithubRepositoryMapping[],
    AxiosError,
    { spaceId: number; organizationId: number; repoIds: string[] }
  >({
    mutationFn: async ({ spaceId, repoIds }) => (await setSpaceRepositories(spaceId, repoIds)).data.repositories,
    // Both lists move on a save - a repository leaves `available` as it joins
    // `attached` - so the space-scoped query is invalidated rather than seeded from the
    // response, which carries only the attached half. The organization-scoped list is
    // invalidated too: the Integrations page reads mappings from it, and the GitHub tab
    // still renders its own heading tags from it.
    onSuccess: (_data, { spaceId, organizationId }) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.githubSpaceRepositories(spaceId) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.githubRepositories(organizationId) });
    },
  });
};

/**
 * The pull requests linked to one task.
 *
 * Kept at the default staleness rather than the 24h `QUERY_CONFIG.STALE_TIME` the
 * org-scoped GitHub queries use: this list changes whenever a webhook arrives, and the
 * task view has no other signal that it did. There is no mutation to invalidate it.
 */
export const useGetTaskPullRequests = (taskId: number | undefined) => {
  return useQuery<TaskPullRequest[], AxiosError>({
    queryKey: queryKeys.taskPullRequests(taskId as number),
    queryFn: async () => (await getTaskPullRequests(taskId as number)).data.pullRequests,
    enabled: !!taskId,
  });
};
